// Ported from mobile src/store/useAnalysisWsStore.ts.
// Only change vs mobile: WsConnectionStatus comes from a local type instead of
// the RN chart types. All ref-counting / routing logic is identical.
import { create } from 'zustand';
import type { WsConnectionStatus } from 'types/connection';
import type { BrainerCoreInner } from 'types/brainerCore';
import type { AnalysisSubKey, AnalysisSubState, AnalysisWsOutbound } from 'types/analysisWs';
import { resolveSubKey } from 'types/analysisWs';

interface AnalysisWsStore {
  // ── Connection ────────────────────────────────────────────────────────────
  connectionStatus: WsConnectionStatus;
  setConnectionStatus: (s: WsConnectionStatus) => void;

  /**
   * Bridge function injected by the WS client when the socket is open.
   * Null while disconnected — consumers must tolerate this.
   */
  sendMessage: ((msg: AnalysisWsOutbound) => void) | null;
  setSendMessage: (fn: ((msg: AnalysisWsOutbound) => void) | null) => void;

  // ── Subscription data ─────────────────────────────────────────────────────
  subscriptions: Record<AnalysisSubKey, AnalysisSubState>;

  /** Ref-count per key so multiple consumers can share a subscription. */
  subscriberCounts: Record<AnalysisSubKey, number>;

  /** Unsubscribe frames buffered while the socket is disconnected. */
  pendingUnsubscribes: AnalysisWsOutbound[];
  queueUnsubscribe: (msg: AnalysisWsOutbound) => void;
  /** Returns the buffered frames and clears the queue. */
  drainPendingUnsubscribes: () => AnalysisWsOutbound[];

  // ── Actions called by the WS client ─────────────────────────────────────────
  receiveAnalysis: (
    symbol: string,
    timeframe: string,
    timestamp: number,
    data: BrainerCoreInner,
    market?: string,
    exchange?: string,
  ) => void;

  // ── Actions called by useAnalysisSubscription ──────────────────────────────
  ensureSubscription: (key: AnalysisSubKey) => void;
  incrementSubscriber: (key: AnalysisSubKey) => void;
  /** Decrements ref-count; returns true when it reaches 0 (last consumer gone). */
  decrementSubscriber: (key: AnalysisSubKey) => boolean;
  markTimeout: (key: AnalysisSubKey) => void;
  resetToLoading: (key: AnalysisSubKey) => void;
  removeSubscription: (key: AnalysisSubKey) => void;

  activeKeys: () => AnalysisSubKey[];
}

export const useAnalysisWsStore = create<AnalysisWsStore>()((set, get) => ({
  connectionStatus: 'disconnected',
  setConnectionStatus: (s) => set({ connectionStatus: s }),

  sendMessage: null,
  setSendMessage: (fn) => set({ sendMessage: fn }),

  subscriptions: {},
  subscriberCounts: {},
  pendingUnsubscribes: [],

  queueUnsubscribe: (msg) => {
    set((state) => ({
      pendingUnsubscribes: [...state.pendingUnsubscribes, msg],
    }));
  },

  drainPendingUnsubscribes: () => {
    const queue = get().pendingUnsubscribes;
    if (queue.length > 0) {
      set({ pendingUnsubscribes: [] });
    }
    return queue;
  },

  receiveAnalysis: (symbol, timeframe, timestamp, data, market, exchange) => {
    // Multiple panels may share a (symbol, timeframe) but differ by market /
    // exchange in the comparison grid, so route precisely using whatever the
    // server frame disambiguates with (market) plus the exchange from the
    // payload, falling back to a prefix match for single-sub cases.
    set((state) => {
      const key = resolveSubKey(
        Object.keys(state.subscriptions),
        symbol,
        timeframe,
        market,
        exchange,
      );
      if (!key) return state;
      return {
        subscriptions: {
          ...state.subscriptions,
          [key]: { status: 'ready', data, timestamp },
        },
      };
    });
  },

  ensureSubscription: (key) => {
    set((state) => {
      if (state.subscriptions[key]) return state; // already exists — no-op
      return {
        subscriptions: {
          ...state.subscriptions,
          [key]: { status: 'loading', data: null, timestamp: null },
        },
      };
    });
  },

  incrementSubscriber: (key) => {
    set((state) => ({
      subscriberCounts: {
        ...state.subscriberCounts,
        [key]: (state.subscriberCounts[key] ?? 0) + 1,
      },
    }));
  },

  decrementSubscriber: (key) => {
    const current = get().subscriberCounts[key] ?? 0;
    const next = Math.max(0, current - 1);
    set((state) => ({
      subscriberCounts: { ...state.subscriberCounts, [key]: next },
    }));
    return next === 0;
  },

  markTimeout: (key) => {
    set((state) => {
      const entry = state.subscriptions[key];
      // Only transition from 'loading' → 'timeout'; ignore stale timers
      if (!entry || entry.status !== 'loading') return state;
      return {
        subscriptions: {
          ...state.subscriptions,
          [key]: { ...entry, status: 'timeout' },
        },
      };
    });
  },

  resetToLoading: (key) => {
    set((state) => {
      const entry = state.subscriptions[key];
      if (!entry) return state;
      return {
        subscriptions: {
          ...state.subscriptions,
          [key]: { ...entry, status: 'loading' },
        },
      };
    });
  },

  removeSubscription: (key) => {
    set((state) => {
      const { [key]: _sub, ...restSubs } = state.subscriptions;
      const { [key]: _count, ...restCounts } = state.subscriberCounts;
      return { subscriptions: restSubs, subscriberCounts: restCounts };
    });
  },

  activeKeys: () => Object.keys(get().subscriptions),
}));
